// Health Watcher — observabilidade unificada + cleanup ativo.
// (Nome do arquivo mantido como long-session-cleaner.ts pra não
// quebrar imports; conceitualmente é um health-watcher consolidado.)
//
// Spec: docs/specs/performance.md §9.5 + roadmap-pos-mvp.md #7.
//
// CONSOLIDA 2 watchdogs antes separados:
// - fps-watchdog.ts (deletado nesta refatoração) — rAF loop + FPS sample.
// - long-session-cleaner.ts antigo — refresh de capabilities + IDB GC + RQ GC.
//
// Por que consolidar:
// - Telemetria correlacionada num evento único (health_sample) — Grafana
//   cruza FPS × heap × IDB × long-session sem JOIN entre séries.
// - 1 fonte de verdade pra dev futuro (lê 1 arquivo, vê tudo).
// - Reduz cadências dispersas (3 timers diferentes antes; 2 agora,
//   uma rAF e um setInterval pesado).
//
// PRINCÍPIO: degradação de UI é mentira pro user. Cleanup ativo é
// solução real. Capabilities NÃO degradam baseadas em longSessionMode.
// Prompt de reload (LongSessionPrompt) só aparece com evidência
// objetiva de problema (heap > threshold).
//
// Cadências:
// - rAF contínuo. MEDIÇÃO de FPS em buckets curtos (~5s); EMISSÃO do
//   `health_sample` agregada a cada 30s (FPS médio + minFps + heap + IDB +
//   longSessionMode + tier). Medição e emissão desacopladas (ver fps-detector).
// - setInterval 5min: refreshCapabilities() — recomputa longSessionMode
//   pra detectar transições normal→warn→degrade→suggest.
// - setInterval 1h: runCleanup() pesado (IDB pruneExpired + RQ GC).
// - Detector de FPS drop sustained: usa o minFps (pior bucket de ~5s) da
//   janela, não a média — drop curto NÃO dilui. 2 janelas baixas → `fps_drop`.

import { getCapability, refreshCapabilities, subscribeCapabilities } from '@app-iptv/device'
import type { LongSessionMode } from '@app-iptv/device'
import type { QueryClient } from '@tanstack/react-query'

import {
  decideFpsDrop,
  FPS_BUCKET_MS,
  FpsWindowAccumulator,
  type FpsDropState,
} from './fps-detector'
import { checkQuotaAndAct } from './idb-quota-manager'
import { flush, track } from './qoe'
import { seriesInfoStore } from './series-info-store'
import { vodInfoStore } from './vod-info-store'

// health_sample EMITIDO a cada 30s. Era 5s, mas a medição de prod mostrou que
// esse evento dominava o volume de QoE (~46% dos eventos) e saturava o Postgres
// compartilhado na escala alvo (100k usuários/provider). 30s corta ~40% do
// volume total sem perda analítica: alertas usam janelas de minutos e
// relatórios usam janelas de dias. Decisão Fernando 10/06/2026.
//
// A EMISSÃO ser 30s NÃO significa MEDIR FPS em janela de 30s: isso diluía
// drops curtos (15s a 8fps + 15s a 60fps ≈ média 34fps, acima do threshold) e
// o detector nunca via o travamento. Agora a medição roda em buckets de ~5s
// (FPS_BUCKET_MS) dentro da janela de 30s e o health_sample carrega o avgFps E
// o minFps (pior bucket). O detector usa o minFps. Ver fps-detector.ts.
const SAMPLE_INTERVAL_MS = 30_000
const REFRESH_INTERVAL_MS = 5 * 60 * 1000 // recompute capabilities 5min
const CLEANUP_INTERVAL_MS = 60 * 60 * 1000 // cleanup pesado 1h

interface MemoryInfo {
  usedJSHeapSize: number
  totalJSHeapSize: number
  jsHeapSizeLimit: number
}

let started = false

// rAF loop state
let rafRunning = false
// Medição de FPS em buckets curtos + emissão agregada a cada 30s. Toda a
// aritmética de janela/bucket/min vive no acumulador puro (testável sem rAF).
let fpsAccumulator = new FpsWindowAccumulator({
  bucketMs: FPS_BUCKET_MS,
  emitMs: SAMPLE_INTERVAL_MS,
})
// Estado do detector de fps_drop entre janelas (streak de janelas baixas).
let fpsDropState: FpsDropState = { lowStreak: 0 }
// Epoch token defende contra race em start→stop→start rápido
// (StrictMode dev, HMR Fast Refresh, remontagem AppProviders): cada
// `start` incrementa, e `tick` valida que ainda é dono da epoch
// antes de continuar. Sem isso, ticks órfãos do start anterior
// continuavam contando frames junto com o novo loop = FPS dobrado
// até stop real. Detectado em code review B1.
let rafEpoch = 0
// Reset de sample após visibilitychange — primeira sample pós-resume
// teria frames=poucos, elapsed=horas → FPS≈0 → spam de fps_drop falso
// (Tizen TV em standby + retorno gera o cenário). Detectado em M2.
let resetOnNextSample = false
// Lifecycle de visibility: subscribe único enquanto cleaner rodando.
let visibilityHandler: (() => void) | null = null

// Timers
let refreshTimer: ReturnType<typeof setInterval> | null = null
let cleanupTimer: ReturnType<typeof setInterval> | null = null
let unsubscribe: (() => void) | null = null
let lastMode: LongSessionMode = 'normal'

// Cache de IDB quota — evita chamar navigator.storage.estimate a cada
// sample (operação relativamente cara). Atualizado no cleanup pesado e
// também a cada N samples no rAF.
const IDB_QUOTA_REFRESH_SAMPLES = 2 // ~1min em samples de 30s
let idbCachedUsageMb: number | null = null
let idbCachedQuotaMb: number | null = null
let idbSampleCounter = 0

/** Listener pro componente UI mostrar prompt em modo 'suggest'.
 *  Pra evitar acoplamento Provider→DOM, watcher só notifica.
 *  LongSessionPrompt faz o health check antes de exibir. */
type SuggestListener = () => void
const suggestListeners = new Set<SuggestListener>()

export function onLongSessionSuggest(cb: SuggestListener): () => void {
  suggestListeners.add(cb)
  return () => suggestListeners.delete(cb)
}

/** Lê heap atual (Chromium-only). null em browsers sem performance.memory. */
function readHeap(): { mb: number; limitMb: number; pressurePct: number } | null {
  const mem = (performance as unknown as { memory?: MemoryInfo }).memory
  if (!mem || !mem.jsHeapSizeLimit) return null
  return {
    mb: Math.round(mem.usedJSHeapSize / 1024 / 1024),
    limitMb: Math.round(mem.jsHeapSizeLimit / 1024 / 1024),
    pressurePct: Math.round((mem.usedJSHeapSize / mem.jsHeapSizeLimit) * 100),
  }
}

/** Atualiza cache de IDB quota (assíncrono, fire-and-forget). */
async function refreshIdbQuota(): Promise<void> {
  if (!navigator.storage?.estimate) return
  try {
    const est = await navigator.storage.estimate()
    if (est.usage != null && est.quota != null && est.quota > 0) {
      idbCachedUsageMb = Math.round(est.usage / 1024 / 1024)
      idbCachedQuotaMb = Math.round(est.quota / 1024 / 1024)
    }
  } catch {
    // ignora — quota API opcional
  }
}

/** rAF tick: alimenta o acumulador de FPS (buckets ~5s). Quando a janela de
 *  emissão (30s) fecha, emite o health_sample agregado (avgFps + minFps) e roda
 *  o detector de fps_drop sobre o minFps. Epoch defende contra ticks órfãos de
 *  start→stop→start rápido. */
function rafTick(now: number, epoch: number): void {
  if (!rafRunning || epoch !== rafEpoch) return

  // Pós-resume de visibility: reancora o acumulador em `now`. Sem isso a
  // primeira janela pós-standby teria elapsed=horas → FPS≈0 → fps_drop falso
  // (Tizen TV voltando de standby).
  if (resetOnNextSample) {
    resetOnNextSample = false
    fpsAccumulator.reset(now)
    requestAnimationFrame((t) => rafTick(t, epoch))
    return
  }

  // Conta o frame; só devolve resultado quando a janela de 30s fecha.
  // (Nome `closedWindow` em vez de `window` pra não sombrear o global.)
  const closedWindow = fpsAccumulator.frame(now)
  if (closedWindow) {
    const tier = getCapability('cpuTier')
    const longSessionMode = getCapability('longSessionMode')
    const heap = readHeap()

    // Refresh IDB quota cache periodicamente (não a cada sample —
    // navigator.storage.estimate é caro).
    idbSampleCounter++
    if (idbSampleCounter >= IDB_QUOTA_REFRESH_SAMPLES) {
      idbSampleCounter = 0
      void refreshIdbQuota()
    }

    // Telemetria correlacionada — 1 evento, todos os sinais juntos.
    // `fps` = média da janela (preserva o baseline histórico do Grafana);
    // `fps_min` = pior bucket de ~5s (sensível a drop curto).
    const meta: Record<string, number | string> = {
      fps_min: closedWindow.minFps,
      fps_buckets: closedWindow.bucketCount,
    }
    if (heap) {
      meta['heap_mb'] = heap.mb
      meta['heap_limit_mb'] = heap.limitMb
      meta['heap_pressure_pct'] = heap.pressurePct
    }
    if (idbCachedUsageMb !== null) meta['idb_usage_mb'] = idbCachedUsageMb
    if (idbCachedQuotaMb !== null) meta['idb_quota_mb'] = idbCachedQuotaMb

    track({
      type: 'health_sample',
      ts: Math.floor(Date.now() / 1000),
      tier,
      fps: closedWindow.avgFps,
      long_session_mode: longSessionMode,
      ...meta,
    })

    // Detector de FPS drop sustained — emit on EDGE (transição alto→baixo ao
    // bater o sustained). Usa o minFps (pior bucket de ~5s), NÃO a média da
    // janela: assim um travamento curto não dilui e dispara o evento. O
    // edge-only evita spam em devices com FPS cronicamente baixo (Tizen 2017
    // + 4K decode gerava 720 events/h). Detectado em code review M1.
    const decision = decideFpsDrop({ minFps: closedWindow.minFps, state: fpsDropState })
    fpsDropState = { lowStreak: decision.lowStreak }
    if (decision.type === 'emit') {
      track({
        type: 'fps_drop',
        fps: decision.minFps,
        tier,
        ts: Math.floor(Date.now() / 1000),
        long_session_mode: longSessionMode,
        ...(heap ? { heap_pressure_pct: heap.pressurePct } : {}),
      })
    }
  }

  requestAnimationFrame((t) => rafTick(t, epoch))
}

/** Cleanup ativo pesado — roda 1×/h. Silencioso, sem feedback ao user.
 *  IDB scan + RQ GC + refresh de quota cache. Try/catch global pra
 *  evitar unhandled rejection (Mi4 do code review). */
async function runCleanup(queryClient: QueryClient): Promise<void> {
  const startedAt = Date.now()
  let vodPruned = 0
  let seriesPruned = 0
  let rqRemoved = 0
  let errorMsg: string | null = null

  try {
    // 1. IDB GC: remove items expirados ou de schema antigo nos stores
    //    de info enriquecida. TTL é checado em cada `get`, mas items
    //    nunca relidos ficavam pra sempre. pruneExpired varre tudo.
    const [vodResult, seriesResult] = await Promise.all([
      vodInfoStore.pruneExpired(),
      seriesInfoStore.pruneExpired(),
    ])
    vodPruned = vodResult
    seriesPruned = seriesResult

    // 2. RQ GC: remove queries INATIVAS (sem observers). Re-checa
    //    observersCount antes do remove pra evitar matar query que
    //    reativou durante a iteração.
    const cache = queryClient.getQueryCache()
    cache.findAll({ type: 'inactive' }).forEach((q) => {
      if (q.getObserversCount() === 0) {
        cache.remove(q)
        rqRemoved++
      }
    })

    // 3. IDB quota check + eviction. checkQuotaAndAct lê
    //    navigator.storage.estimate() e decide:
    //    - <80% quota: no-op (TTL prune já cobre).
    //    - 80-90%: cleanup proativo (re-prune nos info stores).
    //    - >90%: eviction LRU agressiva até cair pra 70%.
    //    Telemetria própria via evento cleanup_sample com fields
    //    quota_action/quota_pressure_before/after.
    await checkQuotaAndAct()

    // 4. Refresh IDB quota cache (atualiza pros próximos health_samples).
    await refreshIdbQuota()
  } catch (err) {
    errorMsg = err instanceof Error ? err.message.slice(0, 100) : 'unknown'
    if (import.meta.env.DEV) {
      console.warn('[health-watcher] cleanup falhou:', err)
    }
  }

  const elapsed = Date.now() - startedAt

  if (import.meta.env.DEV) {
    console.warn(
      `[health-watcher] cleanup: vod=${vodPruned}, series=${seriesPruned}, rq=${rqRemoved}, ${elapsed}ms`,
    )
  }

  const tier = getCapability('cpuTier')
  const heap = readHeap()
  // Discriminator `cleanup_source: 'run-cleanup'` (M5 do code review)
  // pra distinguir do cleanup_sample emitido pelo idb-quota-manager
  // ('quota-manager'). Mesmo type, sources diferentes — Grafana
  // agrega por source pra evitar SUM duplicado.
  track({
    type: 'cleanup_sample',
    ts: Math.floor(Date.now() / 1000),
    tier,
    cleanup_vod_pruned: vodPruned,
    cleanup_series_pruned: seriesPruned,
    cleanup_rq_removed: rqRemoved,
    cleanup_elapsed_ms: elapsed,
    cleanup_source: 'run-cleanup',
    ...(heap ? { heap_pressure_pct_post_cleanup: heap.pressurePct } : {}),
    ...(errorMsg ? { cleanup_error: errorMsg } : {}),
  })
}

/** Teto do último suspiro: quanto o usuário pode esperar pela telemetria depois
 *  de clicar "Recarregar". Curto de propósito — o reload que vem em seguida leva
 *  segundos, então 400ms some na UX; mas o clique NUNCA fica refém da rede. */
const FLUSH_BEFORE_RELOAD_MAX_MS = 400

/**
 * Flush dos eventos QoE pendentes ANTES de um reload deliberado (o usuário
 * clicou "Recarregar" no prompt de long session) — sem isso o evento da própria
 * transição se perde no buffer.
 *
 * SEMPRE resolve, e sempre em ≤400ms. É o único ponto do app que espera por
 * telemetria, e mesmo aqui a espera tem teto: `flush()` pode estar represado
 * pela janela crítica (net-priority, sem teto) ou pendurado numa rede ruim —
 * em nenhum dos casos o botão do usuário pode ficar travado. Perder o último
 * evento é gratuito; travar o clique dele não é.
 */
export async function flushQoe(): Promise<void> {
  try {
    await Promise.race([
      flush().catch(() => undefined),
      new Promise<void>((resolve) => setTimeout(resolve, FLUSH_BEFORE_RELOAD_MAX_MS)),
    ])
  } catch {
    // best-effort
  }
}

export function startLongSessionCleaner(queryClient: QueryClient): void {
  if (started) return
  started = true

  // Estado inicial — capability já calculada.
  lastMode = getCapability('longSessionMode')
  // Edge case: app boot direto em sessão >8h (relógio dessincronizado,
  // recovery de suspensão de TV). Subscribe só notifica em TRANSIÇÕES —
  // sem fire inicial, prompt nunca aparece. Agendamos pra próximo tick
  // pra dar chance dos listeners (LongSessionPrompt) se registrarem.
  if (lastMode === 'suggest') {
    setTimeout(() => {
      for (const cb of [...suggestListeners]) cb()
    }, 0)
  }

  // Subscribe a transições de capabilities (mode change). Capabilities
  // NÃO degradam por mode — mode é só observabilidade + trigger pro prompt.
  unsubscribe = subscribeCapabilities((caps) => {
    if (caps.longSessionMode === lastMode) return
    const prev = lastMode
    lastMode = caps.longSessionMode
    if (import.meta.env.DEV) {
      console.warn(`[health-watcher] mode change: ${prev} → ${lastMode}`)
    }
    track({
      type: 'long_session_transition',
      ts: Math.floor(Date.now() / 1000),
      tier: caps.cpuTier,
      from: prev,
      to: lastMode,
    })
    // Notifica listeners do prompt — eles fazem health check antes de exibir.
    if (lastMode === 'suggest') {
      for (const cb of [...suggestListeners]) cb()
    }
  })

  // Timer 5min: força recompute de capabilities. capabilities re-deriva
  // longSessionMode baseado em Date.now() - sessionStartedAt.
  refreshTimer = setInterval(() => {
    refreshCapabilities()
  }, REFRESH_INTERVAL_MS)

  // Timer 1h: cleanup pesado (IDB scan + RQ GC).
  cleanupTimer = setInterval(() => {
    void runCleanup(queryClient)
  }, CLEANUP_INTERVAL_MS)

  // rAF loop pra FPS + health_sample contínuo. Refresh inicial de IDB
  // quota async pra primeira amostra já ter dado.
  void refreshIdbQuota()
  rafRunning = true
  // Reinstancia o acumulador e zera o detector (start limpo após stop/HMR).
  fpsAccumulator = new FpsWindowAccumulator({
    bucketMs: FPS_BUCKET_MS,
    emitMs: SAMPLE_INTERVAL_MS,
  })
  fpsDropState = { lowStreak: 0 }
  idbSampleCounter = 0
  resetOnNextSample = false
  // Bump epoch — ticks órfãos do start anterior (race em StrictMode/HMR)
  // veem epoch diferente e fazem early return.
  const myEpoch = ++rafEpoch

  // Visibility listener — descarta primeira sample pós-resume (TV de
  // standby retornando, aba volta de hidden). Evita FPS≈0 espúrio.
  visibilityHandler = () => {
    if (!document.hidden) resetOnNextSample = true
  }
  document.addEventListener('visibilitychange', visibilityHandler)

  requestAnimationFrame((t) => rafTick(t, myEpoch))
}

export function stopLongSessionCleaner(): void {
  rafRunning = false
  if (refreshTimer) clearInterval(refreshTimer)
  if (cleanupTimer) clearInterval(cleanupTimer)
  refreshTimer = null
  cleanupTimer = null
  if (unsubscribe) unsubscribe()
  unsubscribe = null
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler)
    visibilityHandler = null
  }
  started = false
}
